// Live artefacts (/v1/workspaces/{id}/live-artefacts): a project's
// LiveReportArtefact promoted to a permanent public URL and served by a
// long-running sandbox on an origin separate from the control plane. Publishing
// SNAPSHOTS the workspace tree rather than pointing at it, so a deployment
// changes only when someone publishes again — a git sync or a file edit never
// reaches the public URL on its own.
//
// There are TWO listing routes and the difference decides where each may be
// called. listDeployments is a plain row read (viewer+) that spawns nothing and
// is safe on page load; discoverArtefacts runs the project indexer inside a
// bino sandbox (member+), so it costs what a lint costs and must only run when
// the user asks to publish something.
//
// Secrets travel one way. The customer's access token is accepted once on write
// and only its SHA-256 is kept — tokenHint (its last four characters) is all
// that ever comes back — and the viewer-link signing key never leaves the
// server, which is why revoking one link is not offered: rotateLinkKey
// invalidates every outstanding link at once.
//
// With deployments on (see ./deployments.ts), a live artefact of a
// repository's workspace is a deployment of that repository, and these
// routes are its aliases: a change needs the maintainer role, and a create
// makes a draft that a maintainer enables on the repository's page. The
// aliases are going away; new code uses the repository routes.

import { getJSON, sendJSON, request } from "./http.js";

const BASE = "/v1/workspaces";

/** Who may view a published deployment. */
export type LiveVisibility = "team" | "token" | "public";

/**
 * LiveStatus is the deployment lifecycle: "draft" was never enabled, "live"
 * is enabled and "stopped" disabled. With deployments off, "draft" exists
 * only between the row landing and its first snapshot — a create whose
 * publish fails deletes the row again — so a listed deployment is in practice
 * "live" or "stopped". With deployments on, a create in a repository's
 * workspace makes a draft that stays one until a maintainer enables it.
 */
export type LiveStatus = "draft" | "live" | "stopped";

/**
 * LiveDeployment is one published artefact. `url` is empty when the server
 * hosts no live URLs (BINO_CLOUD_LIVE_URL unset): an absent field the UI can
 * act on, rather than a link that would 404 in the customer's face.
 */
export interface LiveDeployment {
  id: string;
  workspaceId: string;
  /**
   * The repository the deployment belongs to. Only set with deployments on;
   * its routes are then the canonical ones (./deployments.ts).
   */
  repositoryId?: string;
  artefact: string;
  slug: string;
  url?: string;
  visibility: LiveVisibility;
  hasToken: boolean;
  /** The access token's last four characters; the token itself never returns. */
  tokenHint?: string;
  /** Hosts allowed to iframe the report; empty means no site may frame it. */
  embedDomains: string[];
  /** 0 keeps the instance always-on; > 0 scales it to zero after that delay. */
  idleTimeoutSeconds: number;
  /**
   * How long a device that presented a valid credential stays signed in,
   * renewed slidingly on every visit. 0 means device sessions are off and
   * every browser session needs a fresh credential.
   */
  sessionTtlSeconds: number;
  snapshot?: string;
  /** Empty when the deployment has never been published. */
  snapshotAt: string;
  /** "manual", or the git sha the snapshot was taken from. */
  snapshotSource?: string;
  status: LiveStatus;
  created: string;
  updated: string;
}

/**
 * LiveCandidate is one entry of discovery: a LiveReportArtefact the project
 * currently declares, its deployment, or both. A deployment whose artefact has
 * since vanished from the project comes back with available=false rather than
 * hidden — it is still serving its last snapshot.
 */
export interface LiveCandidate {
  artefact: string;
  file?: string;
  position?: number;
  available: boolean;
  /** The oldest deployment of the artefact. */
  deployment?: LiveDeployment;
  /**
   * Every deployment of the artefact, oldest first; never absent. With
   * deployments on, an artefact may have several, at different commits.
   */
  deployments: LiveDeployment[];
}

export interface PublishRequest {
  artefact: string;
  visibility: LiveVisibility;
  /** Required for "token" visibility, 16-256 characters. Accepted only here. */
  token?: string;
  embedDomains?: string[];
  /** 0 means always-on, which the operator rations by count. */
  idleTimeoutSeconds?: number;
  /**
   * Device-session lifetime in seconds, sliding — every visit renews it for
   * this long again. 0 disables device sessions; absent means the default of
   * 30 days.
   */
  sessionTtlSeconds?: number;
}

/**
 * UpdateRequest changes how a deployment is SERVED. Every field is optional
 * because absent means "leave alone" on the wire — which is what lets a caller
 * change the visibility without resending an access token it can no longer read
 * back.
 */
export interface UpdateRequest {
  visibility?: LiveVisibility;
  /** Replaces the stored access token; "" drops it. Accepted only here and on create. */
  token?: string;
  embedDomains?: string[];
  idleTimeoutSeconds?: number;
  /**
   * Device-session lifetime in seconds, sliding. 0 disables device sessions
   * (an absolute kill switch — outstanding cookies stop verifying); lowering
   * it likewise applies to cookies already handed out.
   */
  sessionTtlSeconds?: number;
}

export interface LiveToken {
  token: string;
  expiresAt: string;
}

export interface ViewerLink {
  url: string;
  expiresAt: string;
}

/**
 * listDeployments returns the workspace's deployments, oldest first (viewer+).
 * Rows only — it spawns nothing, which is what makes it the safe one to call
 * whenever the workspace page loads.
 */
export async function listDeployments(id: string): Promise<LiveDeployment[]> {
  const { items } = await getJSON<{ items: LiveDeployment[] }>(
    `${BASE}/${encodeURIComponent(id)}/live-artefacts/deployments`,
  );
  return items;
}

/**
 * discoverArtefacts returns every LiveReportArtefact the workspace's CURRENT
 * tree declares, joined with its deployment (member+). It runs the project
 * indexer in a sandbox, so treat it as the publish picker's on-demand call and
 * never as something a page load or a poll may trigger.
 */
export async function discoverArtefacts(id: string): Promise<LiveCandidate[]> {
  const { items } = await getJSON<{ items: LiveCandidate[] }>(
    `${BASE}/${encodeURIComponent(id)}/live-artefacts`,
  );
  return items;
}

/**
 * createDeployment creates the deployment and takes its first snapshot in one
 * call (member+). A failed publish leaves nothing behind, so a retry is a
 * retry rather than a conflict. With deployments on, a workspace of a
 * repository gets a draft of the repository instead (member+), which takes no
 * snapshot and serves nothing until a maintainer enables it; any other
 * workspace then needs the maintainer role.
 */
export function createDeployment(id: string, req: PublishRequest): Promise<LiveDeployment> {
  return sendJSON<LiveDeployment>(`${BASE}/${encodeURIComponent(id)}/live-artefacts`, "POST", req);
}

/**
 * updateDeployment changes the SERVING settings of a deployment that already
 * exists — visibility, access token, embed allow-list, idle timeout (member+;
 * maintainer+ with deployments on). It is the only way to alter them:
 * publishing re-snapshots and touches none of them, and deleting draws a new
 * slug, which breaks every URL already handed out.
 */
export function updateDeployment(
  id: string,
  laId: string,
  req: UpdateRequest,
): Promise<LiveDeployment> {
  return sendJSON<LiveDeployment>(laPath(id, laId), "PATCH", req);
}

/**
 * publishDeployment re-snapshots the workspace and rolls the deployment onto
 * the new copy (member+; maintainer+ with deployments on). The previous
 * snapshot keeps serving until this succeeds.
 */
export function publishDeployment(id: string, laId: string): Promise<LiveDeployment> {
  return sendJSON<LiveDeployment>(`${laPath(id, laId)}/publish`, "POST", {});
}

/** stopDeployment takes the deployment out of service and kills its instance (member+; maintainer+ with deployments on); the snapshot is kept. */
export function stopDeployment(id: string, laId: string): Promise<LiveDeployment> {
  return sendJSON<LiveDeployment>(`${laPath(id, laId)}/stop`, "POST", {});
}

/** startDeployment puts a stopped deployment back in service (member+; maintainer+ with deployments on); the instance starts on the first visitor. */
export function startDeployment(id: string, laId: string): Promise<LiveDeployment> {
  return sendJSON<LiveDeployment>(`${laPath(id, laId)}/start`, "POST", {});
}

/** deleteDeployment removes the deployment, its snapshots and its instance (maintainer+); the public URL stops resolving. */
export async function deleteDeployment(id: string, laId: string): Promise<void> {
  await request(laPath(id, laId), { method: "DELETE" });
}

/**
 * mintLiveToken issues the short-lived token a browser attaches as `?t=` to
 * reach a team-visibility deployment (viewer+), since a new tab cannot set an
 * Authorization header. It authorizes nothing by itself — the caller's team
 * role is re-resolved on every served request.
 */
export function mintLiveToken(id: string, laId: string): Promise<LiveToken> {
  return sendJSON<LiveToken>(`${laPath(id, laId)}/token`, "POST", {});
}

/**
 * mintViewerLink signs one per-viewer URL for an end user of the CUSTOMER's —
 * someone bino Cloud holds no account for (member+; maintainer+ with
 * deployments on). The params are signed into the link and forced onto the
 * served request, overriding anything the visitor appends, so their values
 * are enforced rather than advisory. They confine
 * rows only where a DataSet's SQL filters on them: a component filter runs in
 * the viewer's browser, so every row of its dataset reaches the viewer. A link
 * cannot be revoked on its own; see rotateLinkKey.
 */
export function mintViewerLink(
  id: string,
  laId: string,
  req: { ttlSeconds?: number; params?: Record<string, string> },
): Promise<ViewerLink> {
  return sendJSON<ViewerLink>(`${laPath(id, laId)}/links`, "POST", req);
}

/** rotateLinkKey draws a new signing key, invalidating EVERY outstanding viewer link at once (maintainer+). */
export async function rotateLinkKey(id: string, laId: string): Promise<void> {
  await sendJSON(`${laPath(id, laId)}/link-key/rotate`, "POST", {});
}

/** laPath builds one deployment's route base. */
function laPath(id: string, laId: string): string {
  return `${BASE}/${encodeURIComponent(id)}/live-artefacts/${encodeURIComponent(laId)}`;
}
